import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import {
  useArchiveGHADiscountRule,
  useCreateGHADiscountRule,
  useGetGHADiscountRules,
} from '~/hooks/apis/connection-google/useGHADiscountRules';

import {
  CreateGHADiscountRuleBody,
  GHAAccountType,
  GHADiscountRule,
  GHAProperty,
} from '~/services/ConnectionGoogleService';

import GHADiscountRuleDialog from './GHADiscountRuleDialog';
import GHADiscountTokenDialog from './GHADiscountTokenDialog';
import { formatDateTime, formatDiscountAccountType, formatRuleDiscount } from './utils';

interface Props {
  open: boolean;
  property: GHAProperty | null;
  accountTypes: Array<GHAAccountType>;
  selectedAccountType?: GHAAccountType;
  onClose: () => void;
  onRulesChanged: () => Promise<void>;
}

const GENERIC_CREATE_ERROR =
  'Couldn’t create — an active discount rule may already exist for these thresholds; archive it first.';
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function isCountrySpecificRule(rule: GHADiscountRule) {
  return Boolean(rule.countryDiscounts && Object.keys(rule.countryDiscounts).length > 0);
}

function getRuleDiscountDetails(rule: GHADiscountRule) {
  if (!rule.countryDiscounts || Object.keys(rule.countryDiscounts).length === 0) {
    return formatRuleDiscount(rule);
  }

  return Object.entries(rule.countryDiscounts)
    .sort(([countryCodeA], [countryCodeB]) => countryCodeA.localeCompare(countryCodeB))
    .map(([countryCode, discount]) =>
      discount.type === 'margin_retention'
        ? `${countryCode}: Retain ${discount.value}% margin`
        : `${countryCode}: ${discount.value}% off`,
    )
    .join('\n');
}

function getDiscountValueColumn(): GridColDef<GHADiscountRule> {
  return {
    field: 'discountValue',
    headerName: 'Value',
    flex: 1,
    minWidth: 220,
    valueGetter: (_value, row) => formatRuleDiscount(row),
    renderCell: ({ row }) => {
      const value = formatRuleDiscount(row);
      const tooltipTitle = getRuleDiscountDetails(row);

      return (
        <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</Box>} arrow>
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              display: 'flex',
              font: 'inherit',
              height: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {value}
          </Box>
        </Tooltip>
      );
    },
  };
}

function getActiveColumns(
  onArchive: (rule: GHADiscountRule) => void,
  onGenerateToken: (rule: GHADiscountRule) => void,
): Array<GridColDef<GHADiscountRule>> {
  return [
    {
      field: 'accountType',
      headerName: 'Account Type',
      width: 160,
      valueGetter: (value) => formatDiscountAccountType(value),
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 150,
      valueGetter: (_value, row) => (isCountrySpecificRule(row) ? 'Country-specific' : 'Universal'),
    },
    getDiscountValueColumn(),
    { field: 'minAdvanceBookingWindow', headerName: 'Min ABW', width: 110 },
    { field: 'minLengthOfStay', headerName: 'Min LOS', width: 110 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 170,
      valueGetter: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 110,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="generate-token"
          label="Generate test token"
          onClick={() => onGenerateToken(row)}
          showInMenu
        />,
        <GridActionsCellItem key="archive" label="Archive" onClick={() => onArchive(row)} showInMenu />,
      ],
    },
  ];
}

function getArchivedColumns(): Array<GridColDef<GHADiscountRule>> {
  return [
    {
      field: 'accountType',
      headerName: 'Account Type',
      width: 160,
      valueGetter: (value) => formatDiscountAccountType(value),
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 150,
      valueGetter: (_value, row) => (isCountrySpecificRule(row) ? 'Country-specific' : 'Universal'),
    },
    getDiscountValueColumn(),
    { field: 'minAdvanceBookingWindow', headerName: 'Min ABW', width: 110 },
    { field: 'minLengthOfStay', headerName: 'Min LOS', width: 110 },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 170,
      valueGetter: (value) => formatDateTime(value),
    },
    {
      field: 'updatedAt',
      headerName: 'Archived At',
      width: 170,
      valueGetter: (value) => formatDateTime(value),
    },
  ];
}

function GHAPropertyDiscountRulesDialog({
  open,
  property,
  accountTypes,
  selectedAccountType,
  onClose,
  onRulesChanged,
}: Props) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [ruleToArchive, setRuleToArchive] = useState<GHADiscountRule | null>(null);
  const [ruleForToken, setRuleForToken] = useState<GHADiscountRule | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [archivedVisited, setArchivedVisited] = useState(false);
  const [countryFilter, setCountryFilter] = useState<string | null>(null);

  const [activePagination, setActivePagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [archivedPagination, setArchivedPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Reset tab + pagination + lazy-fetch flag whenever the dialog's context
  // changes (different property, different account-type filter), so we don't
  // request page 2 of a list we haven't started paging through yet.
  useEffect(() => {
    setActiveTab('active');
    setArchivedVisited(false);
    setActivePagination({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    setArchivedPagination({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    setCountryFilter(null);
  }, [property?.id, selectedAccountType]);

  useEffect(() => {
    setActivePagination((prev) => ({ ...prev, page: 0 }));
    setArchivedPagination((prev) => ({ ...prev, page: 0 }));
  }, [countryFilter]);

  const allowedAccountTypes = useMemo(
    () => (selectedAccountType ? [selectedAccountType] : accountTypes),
    [accountTypes, selectedAccountType],
  );
  const accountTypeOptions = useMemo(
    () =>
      allowedAccountTypes.map((value) => ({
        value,
        label: formatDiscountAccountType(value),
      })),
    [allowedAccountTypes],
  );

  const baseFilters = useMemo(
    () => ({
      propertyId: property?.id,
      accountType: selectedAccountType,
      country: countryFilter?.length === 2 ? countryFilter : undefined,
    }),
    [countryFilter, property?.id, selectedAccountType],
  );

  const activeQuery = useGetGHADiscountRules(
    {
      ...baseFilters,
      enabled: true,
      page: activePagination.page + 1,
      limit: activePagination.pageSize,
    },
    { enabled: open && !!property?.id },
  );

  const archivedQuery = useGetGHADiscountRules(
    {
      ...baseFilters,
      enabled: false,
      page: archivedPagination.page + 1,
      limit: archivedPagination.pageSize,
    },
    { enabled: open && !!property?.id && archivedVisited },
  );

  const createRuleMutation = useCreateGHADiscountRule();
  const archiveRuleMutation = useArchiveGHADiscountRule();
  const isSaving = createRuleMutation.isPending;

  const handleTabChange = (_event: React.SyntheticEvent, value: 'active' | 'archived') => {
    setActiveTab(value);
    if (value === 'archived') {
      setArchivedVisited(true);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateError(null);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateError(null);
  };

  const handleClose = () => {
    handleCloseCreateDialog();
    setRuleToArchive(null);
    setRuleForToken(null);
    onClose();
  };

  const handleCreate = async (input: CreateGHADiscountRuleBody) => {
    setCreateError(null);
    try {
      await createRuleMutation.mutateAsync(input);
      setCreateDialogOpen(false);
      await onRulesChanged();
    } catch {
      // Backend returns a generic 500 for duplicates (no 409); show a generic
      // inline message and keep the dialog open so the user can correct.
      setCreateError(GENERIC_CREATE_ERROR);
    }
  };

  const handleArchiveRequest = useCallback((rule: GHADiscountRule) => {
    setRuleToArchive(rule);
  }, []);

  const handleGenerateTokenRequest = useCallback((rule: GHADiscountRule) => {
    setRuleForToken(rule);
  }, []);

  const handleArchiveConfirm = async () => {
    if (!ruleToArchive) {
      return;
    }
    try {
      await archiveRuleMutation.mutateAsync({ id: ruleToArchive.id });
      setRuleToArchive(null);
      await onRulesChanged();
    } catch {
      // Snackbar shown by the hook.
    }
  };

  const activeColumns = useMemo(
    () => getActiveColumns(handleArchiveRequest, handleGenerateTokenRequest),
    [handleArchiveRequest, handleGenerateTokenRequest],
  );
  const archivedColumns = useMemo(() => getArchivedColumns(), []);
  const canCreateRule = property !== null && accountTypeOptions.length > 0;

  const activeRows = activeQuery.data?.result ?? [];
  const activeTotal = activeQuery.data?.total ?? 0;
  const archivedRows = archivedQuery.data?.result ?? [];
  const archivedTotal = archivedQuery.data?.total;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>GHA Discount Rules</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1">{property?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {property?.id}
              </Typography>
            </Box>
            {accountTypeOptions.length === 0 && (
              <Alert severity="warning">This property has no account type for the current brand context.</Alert>
            )}
            {activeQuery.isError && (
              <Alert severity="error">
                {activeQuery.error instanceof Error
                  ? activeQuery.error.message
                  : 'Failed to fetch active GHA discount rules'}
              </Alert>
            )}
            {archivedQuery.isError && (
              <Alert severity="error">
                {archivedQuery.error instanceof Error
                  ? archivedQuery.error.message
                  : 'Failed to fetch archived GHA discount rules'}
              </Alert>
            )}

            <Box>
              <Box sx={{ mb: 2, maxWidth: 320 }}>
                <TextField
                  label="Filter by country"
                  value={countryFilter ?? ''}
                  onChange={(event) => setCountryFilter(event.target.value.toUpperCase() || null)}
                  helperText="2-letter ISO country code"
                  inputProps={{ maxLength: 2 }}
                  fullWidth
                />
              </Box>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="active" label={activeQuery.data ? `Active (${activeTotal})` : 'Active'} />
                <Tab
                  value="archived"
                  label={archivedTotal !== undefined ? `Archived (${archivedTotal})` : 'Archived'}
                />
              </Tabs>
              {activeTab === 'active' ? (
                <DataGrid
                  rows={activeRows}
                  columns={activeColumns}
                  loading={activeQuery.isFetching || archiveRuleMutation.isPending}
                  autoHeight
                  disableRowSelectionOnClick
                  paginationMode="server"
                  rowCount={activeTotal}
                  paginationModel={activePagination}
                  onPaginationModelChange={setActivePagination}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  slots={{ pagination: GridPagination }}
                />
              ) : (
                <DataGrid
                  rows={archivedRows}
                  columns={archivedColumns}
                  loading={archivedQuery.isFetching}
                  autoHeight
                  disableRowSelectionOnClick
                  paginationMode="server"
                  rowCount={archivedTotal ?? 0}
                  paginationModel={archivedPagination}
                  onPaginationModelChange={setArchivedPagination}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  slots={{ pagination: GridPagination }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleClose}>
            Close
          </Button>
          <Button variant="contained" onClick={handleOpenCreateDialog} disabled={!canCreateRule}>
            Create Rule
          </Button>
        </DialogActions>
      </Dialog>

      <GHADiscountRuleDialog
        open={createDialogOpen}
        initialPropertyId={property?.id}
        lockPropertyId
        accountTypeOptions={accountTypeOptions}
        defaultAccountType={accountTypeOptions[0]?.value}
        isSaving={isSaving}
        submitError={createError}
        onClose={handleCloseCreateDialog}
        onCreate={handleCreate}
      />

      <GHADiscountTokenDialog open={!!ruleForToken} rule={ruleForToken} onClose={() => setRuleForToken(null)} />

      <Dialog
        open={!!ruleToArchive}
        onClose={archiveRuleMutation.isPending ? undefined : () => setRuleToArchive(null)}
        disableEscapeKeyDown={archiveRuleMutation.isPending}
      >
        <DialogTitle>Archive discount rule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This archives the whole rule. To change one country or value, archive this rule and create a replacement.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRuleToArchive(null)} disabled={archiveRuleMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleArchiveConfirm}
            disabled={archiveRuleMutation.isPending}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GHAPropertyDiscountRulesDialog;
